import { checkLookoutSession, LookoutSessionError } from './lookout-session';
import { getSessionHandoff } from './rudderstack-auth-client';

/**
 * - `renewed`: Lookout's session now runs on a newer ID token.
 * - `current`: the shared session lives for the same user, and Lookout's session needs no renewal yet.
 * - `switched`: Lookout's session belongs to another user now, for example after an account change on the web app, or
 *   because another tab already changed it. So the page reloads.
 * - `ended`: the shared session ended, because the user signed out on either site, or an account change ended it.
 * - `unavailable`: config-backend or Lookout did not answer. The next check tries again.
 */
export type RenewOutcome = 'renewed' | 'current' | 'switched' | 'ended' | 'unavailable';

/**
 * Checks Lookout's session against config-backend's shared session (ACT2-879). Each check asks for a one-time code, so a
 * 401 shows that the shared session ended. Lookout's server compares the code's user with the session's user, and it
 * renews only in the last minutes of the session, or when `force` asks for it, as a re-auth does: Lookout's saved copy
 * of the token can be gone. `userId` is the user that the page shows.
 */
export async function renewLookoutSession(
  apiBaseUrl: string,
  options: { force?: boolean; userId?: string } = {},
): Promise<RenewOutcome> {
  const handoff = await getSessionHandoff(apiBaseUrl);
  if (handoff.kind !== 'code') return handoff.kind;
  try {
    const check = await checkLookoutSession(handoff.code, { renew: options.force === true });
    // Another tab can change the browser's session to another user first. Then this check finds that user's session.
    if (options.userId !== undefined && check.userId !== options.userId) return 'switched';
    if (check.outcome === 'current' || check.outcome === 'renewed') return check.outcome;
    // The server found no session of this page's user, so the page can show another user's data.
    return 'switched';
  } catch (error) {
    // A sign-out in this browser ended Lookout's session while the check was on its way.
    if (error instanceof LookoutSessionError && error.code === 'session_ended') return 'ended';
    return 'unavailable';
  }
}
